import type {Product, ProductImage, ProductStatus, PssOptions, RefurbishedInfo} from '../../domain/entities/Product';


export class ProductDto {
    constructor(
        public readonly id: string,
        public readonly name: string,
        public readonly brandName: string,
        public readonly modelNo: string,
        public readonly description: string,
        public readonly costPrice: number,
        public readonly retailPrice: number,
        public readonly maxPaymentPeriod: number,
        public readonly images: ProductImage[],
        public readonly categories: string[],
        public readonly merchantShopId: string,
        public readonly shopName: string,
        public readonly shopLogo: string,
        public readonly isFeatured: boolean,
        public readonly status: ProductStatus,
        public readonly discountPercentage: number,
        public readonly formattedPrice: string,
        public readonly isAvailable: boolean,
        public readonly isEligibleForPaymentPlan: boolean,
        public readonly createdTime: string,
        // New API-compatible fields
        public readonly imageUrl: string,
        public readonly imageAltUrls: string[],
        public readonly pricing: ProductPricing,
        public readonly stockStatus: string,
        public readonly tags: string[],
        public readonly currency: string,
        public readonly pssOptions?: PssOptions,
        public readonly ratingAverage?: number,
        public readonly refurbishedInfo?: RefurbishedInfo
    ) {
    }

    static fromDomain(product: Product): ProductDto {
        const mainImage = product.images && product.images.length > 0 ? product.images[0]?.image || '' : '';
        const imageAltUrls = product.images ? product.images.map(img => img.image) : [];
        const pricing: ProductPricing = {
            displayPrice: product.retail_price.toString(),
            currency: product.currency,
            compareAtPrice: product.compare_at_price?.toString(),
            isDiscounted: product.isDiscounted(),
            discount: product.isDiscounted() ? {
                amount: product.getDiscountAmount().toString(),
                percent: product.calculateDiscountPercentage()
            } : undefined
        };

        return new ProductDto(
            product.id,
            product.name,
            product.brand_name,
            product.model_no,
            product.description,
            product.cost_price,
            product.retail_price,
            product.max_payment_period,
            product.images,
            product.categories,
            product.merchant_shop_id,
            product.shop_name,
            product.shop_logo,
            product.is_featured,
            product.status,
            product.calculateDiscountPercentage(),
            product.getFormattedPrice(),
            product.isAvailable(),
            product.isEligibleForPaymentPlan(),
            product.created_time.toISOString(),
            mainImage,
            imageAltUrls,
            pricing,
            product.isAvailable() ? 'in_stock' : 'out_of_stock',
            product.tags,
            product.currency,
            product.pss_options,
            product.rating_average,
            product.refurbished_info
        );
    }

    static fromDomainList(products: Product[]): ProductDto[] {
        return products.map(product => ProductDto.fromDomain(product));
    }
}

export interface ProductFiltersRequest {
    categories?: string[];
    brandName?: string;
    minPrice?: number;
    maxPrice?: number;
    isFeatured?: boolean;
    limit?: number;
    offset?: number;
    sortBy?: 'price' | 'name' | 'created' | 'featured';
    sortOrder?: 'asc' | 'desc';
}

export interface ProductSearchRequest {
    query: string;
    filters?: ProductFiltersRequest;
}

export interface ProductPricing {
    displayPrice: string;
    currency: string;
    compareAtPrice?: string;
    isDiscounted: boolean;
    discount?: {
        amount: string;
        percent: number;
    };
}